import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';
import { jwtDecode } from "jwt-decode";
import { Layout, Input, DatePicker, Table, Space, Select, Tag, message, Button, Drawer, Form, Modal, Card, Typography } from 'antd';
import { EditOutlined, CheckCircleOutlined, PrinterOutlined, FileExcelOutlined, ReloadOutlined, FormOutlined } from '@ant-design/icons';
import withRoleAccess from '../../hoc/withRoleAccess';
import './Registrations.css';
import dayjs from 'dayjs';
import FeedbackPrintModal from './FeedbackPrintModal';
import hash from 'hash-sum';

const { Content } = Layout;
const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const StudentFeedbackReport = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [filteredFeedbacks, setFilteredFeedbacks] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchFeedbackId, setSearchFeedbackId] = useState('');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [printModalVisible, setPrintModalVisible] = useState(false);
  const [printRecord, setPrintRecord] = useState(null);
  const [editDrawerVisible, setEditDrawerVisible] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [feedbackFormData, setFeedbackFormData] = useState({
    attendance: '',
    participation: '',
    comprehension: '',
    focus: '',
    progress: '',
    improvement: '',
    comment: ''
  });

  const { t } = useTranslation();

    const token = localStorage.getItem("token");
          let userRole = null;
          let userBranchId = null;
      
          if (token) {
          try {
              const decoded = jwtDecode(token);
              userRole = decoded.role;
              // Support both formats just in case
              userBranchId = decoded.branchId || decoded.branch_id || null;
          } catch (err) {
              console.error("Invalid token", err);
          }
          }

  const API_URL = process.env.REACT_APP_API_URL;

  const itemsPerPage = 50;


  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return isNaN(date) ? 'Invalid date' : date.toLocaleDateString(t('locale'), { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase();
  };


  useEffect(() => {
  const fetchBranches = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/branch`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBranches(res.data);
    } catch (err) {
      console.error("Failed to fetch branches", err);
    }
  };

  fetchBranches();
}, [API_URL]);


const fetchFeedbacks = useCallback(async () => {
  try {
    const token = localStorage.getItem('token');
    const res = await axios.get(`${API_URL}/student-feedback`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setFeedbacks(res.data);
    setFilteredFeedbacks(res.data); // initial load
  } catch (err) {
    console.error('❌ Error fetching feedbacks:', err);
  }
}, [API_URL]);


useEffect(() => {
  fetchFeedbacks();
}, [fetchFeedbacks]);

useEffect(() => {
  let filtered = feedbacks;

  // 🔍 Student name search
  if (searchTerm) {
    const lowerSearch = searchTerm.toLowerCase();
    filtered = filtered.filter(f =>
      f.student_nickname?.toLowerCase().includes(lowerSearch) ||
      f.student_firstname?.toLowerCase().includes(lowerSearch) ||
      f.student_lastname?.toLowerCase().includes(lowerSearch)
    );
  }

  // 🔎 Feedback ID search
  if (searchFeedbackId) {
    filtered = filtered.filter(f =>
      generateFeedbackUID(f).includes(searchFeedbackId.trim().toLowerCase())
    );
  }

  // 📅 Date filters
  if (fromDate) {
    filtered = filtered.filter(f => new Date(f.date) >= fromDate);
  }
  if (toDate) {
    filtered = filtered.filter(f => new Date(f.date) <= toDate);
  }

  // 🏢 Branch filter
  if (selectedBranch) {
    filtered = filtered.filter(f => f.branch_id === selectedBranch);
  }

  setFilteredFeedbacks(filtered);
  setCurrentPage(1);
}, [searchTerm, searchFeedbackId, fromDate, toDate, feedbacks, selectedBranch]);



const handleEdit = async (record) => {
  const token = localStorage.getItem('token');
  try {
    const res = await axios.get(
      `${API_URL}/student-feedback/by-classid-student/${record.classid}/${record.student_id}`,
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );

    const feedback = res.data;
    setSelectedFeedback(feedback);
    setFeedbackFormData({
      attendance: feedback.attendance || '',
      participation: feedback.participation || '',
      comprehension: feedback.comprehension || '',
      focus: feedback.focus || '',
      progress: feedback.progress || '',
      improvement: feedback.improvement || '',
      comment: feedback.comment || ''
    });
    setEditDrawerVisible(true);
  } catch (err) {
    console.error('Failed to fetch feedback for editing:', err);
    message.error('Could not load feedback for editing.');
  }
};


const handleSubmitFeedbackUpdate = async () => {
  if (!selectedFeedback?.id) return;

  try {
    const token = localStorage.getItem('token');
    await axios.put(`${API_URL}/student-feedback/${selectedFeedback.id}`, feedbackFormData, {
      headers: { Authorization: `Bearer ${token}` }
    });

    message.success('Feedback updated successfully');
    
    setEditDrawerVisible(false); // Hide drawer first
    await fetchFeedbacks();      // ✅ Refetch feedbacks after closing
  } catch (error) {
    console.error('Failed to update feedback:', error);
    message.error('Failed to update feedback');
  }
};


const handleApprove = (record) => {
  const currentStatus = record.approve;
  const action = currentStatus === 'approved' ? 'revert to pending' : 'mark as approved';

  Modal.confirm({
    title: 'Confirm Approval Change',
    content: `Are you sure you want to ${action} for this feedback?`,
    okText: 'Yes',
    cancelText: 'No',
    onOk: async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.put(
          `${API_URL}/student-feedback/approve/${record.id}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const newStatus = res.data.status;
        message.success(`Feedback marked as "${newStatus}"`);

        await fetchFeedbacks(); 
      } catch (error) {
        console.error('Error toggling feedback status:', error);
        message.error('Failed to update approval status');
      }
    }
  });
};

  const generateFeedbackUID = (feedback) => {
  return hash({
    id: feedback.id,                       
    classid: feedback.classid,
    student_id: feedback.student_id,
    teacher_id: feedback.teacher_id,
    created_at: feedback.created_at,
  });
};

  const currentItems = filteredFeedbacks.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

       const branchColors = {
  1: "blue",
  2: "green",
  3: "magenta", 
};

  const columns = [
      {
            title: t("Branch"),
            key: "branch",
            dataIndex: "branch_name", 
            width: 120,
            ellipsis: false,
            render: (_, record) => {
              const branchId = record.branch_id;
              const color = branchColors[branchId] || "default";
              return record.branch_name ? (
                <Tag color={color}>{record.branch_name}</Tag>
              ) : (
                <Tag>-</Tag>
              );
            },
          },
    {
    title: t('ClassId'),
    dataIndex: 'classid',
    key: 'classid',
    ellipsis: true
  },
  {
    title: t('Class Code'),
    dataIndex: 'class_code',
    key: 'class_code',
    ellipsis: true,
    width: 200
  },
  {
    title: t('Subject'),
    dataIndex: 'subject_name',
    key: 'subject_name',
    ellipsis: true
  },
{
  title: t('Date - Time'),
  key: 'date',
  ellipsis: true,
  width: 200,
  render: (record) => {
    const timeStr = record.schedule_time;
    const minutes = Number(record.schedule_hour);
    const date = dayjs(record.date); // ← use `record.date` here

    const [hour, min] = timeStr.split(':');
    const start = new Date(2000, 0, 1, +hour, +min);
    const end = new Date(start.getTime() + minutes * 60000);

    const pad = (n) => n.toString().padStart(2, '0');

    return (
      <div>
        <div>{date.format('DD MMM YYYY')}</div>
        <div>{`${pad(start.getHours())}:${pad(start.getMinutes())} - ${pad(end.getHours())}:${pad(end.getMinutes())}`}</div>
        <div style={{ fontSize: '11px', color: '#888' }}>
          {t('Duration')}: {minutes} {t('minutes')}
        </div>
      </div>
    );
  }
},
 {
  title: t('Teacher'),
  key: 'teacher',
  ellipsis: true,
  width: 200,
  render: (record) => (
    <div>
      <div>{`${record.teacher_firstname || ''} ${record.teacher_lastname || ''}`.trim()}</div>
      <div style={{ fontSize: '11px', color: '#888' }}>
        {record.teacher_nickname ? `(${record.teacher_nickname})` : ''}
      </div>
    </div>
  )
},
  {
  title: t('Student'),
  key: 'student',
  ellipsis: true,
  width: 200,
  render: (record) => (
    <div>
      <div>{`${record.student_firstname || ''} ${record.student_lastname || ''}`.trim()}</div>
      <div style={{ fontSize: '11px', color: '#888' }}>
        {record.student_nickname ? `(${record.student_nickname})` : ''}
      </div>
    </div>
  )
},
  {
    title: t('Approval'),
    dataIndex: 'approve',
    key: 'approve',
    render: (status) =>
      status === 'approved' ? (
        <Tag color="green">Approved</Tag>
      ) : (
        <Tag color="orange">Pending</Tag>
      ),
    ellipsis: true
  },
 {
  title: t('Attendance'),
  dataIndex: 'attendance',
  key: 'attendance',
  render: (text) => (
    <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', width: '300px' }}>{text}</div>
  )
},
{
  title: t('Participation'),
  dataIndex: 'participation',
  key: 'participation',
  render: (text) => (
    <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', width: '300px' }}>{text}</div>
  )
},
{
  title: t('Comprehension'),
  dataIndex: 'comprehension',
  key: 'comprehension',
  render: (text) => (
    <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', width: '300px' }}>{text}</div>
  )
},
{
  title: t('Focus'),
  dataIndex: 'focus',
  key: 'focus',
  render: (text) => (
    <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', width: '300px' }}>{text}</div>
  )
},
{
  title: t('Progress'),
  dataIndex: 'progress',
  key: 'progress',
  render: (text) => (
    <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', width: '300px' }}>{text}</div>
  )
},
{
  title: t('Improvement'),
  dataIndex: 'improvement',
  key: 'improvement',
  render: (text) => (
    <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', width: '300px' }}>{text}</div>
  )
},
{
  title: t('Comment'),
  dataIndex: 'comment',
  key: 'comment',
  render: (text) => (
    <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', width: '300px' }}>{text}</div>
  )
},
{
    title: t('created_by'),
    dataIndex: 'created_by',
    key: 'created_by',
    ellipsis: true,
    width: 150
  },
  {
    title: t('updated_by'),
    dataIndex: 'updated_by_name',
    key: 'updated_by_name',
    ellipsis: true,
    width: 150,
     render: (text, record) => (
    <div>
      <div>{text || '-'}</div>
      <div style={{ fontSize: '11px', color: '#888' }}>
        {record.updated_on ? formatDate(record.updated_on) : ''}
      </div>
    </div>
  )
  },
  {
    title: t('approved_by'),
    dataIndex: 'approved_by_name',
    key: 'approved_by_name',
    ellipsis: true,
    width: 150
  },
  {
  title: t('Feedback Ref'),
  key: 'feedback_ref',
  width: 150,
  render: (_, record) => (
    <div style={{ fontSize: '12px', color: '#555' }}>
      {generateFeedbackUID(record)}
    </div>
  )
},

{
  title: t('Action'),
  key: 'action',
  fixed: 'right',
  render: (record) => (
    <Space>
      <Button
        type="link"
        icon={<EditOutlined />}
        onClick={() => handleEdit(record)}
        aria-label="Edit"
      />
      <Button
        type="link"
        icon={<CheckCircleOutlined />}
        onClick={() => handleApprove(record)}
        aria-label="Approve"
      />
      <Button
        type="link"
        icon={<PrinterOutlined />}
        onClick={() => {
            setPrintRecord(record);
            setPrintModalVisible(true);
        }}
        aria-label="Print"
        />
    </Space>
  )
}

  ];

  const handleDateRangeChange = (dates) => {
    setFromDate(dates ? dates[0].toDate() : null);
    setToDate(dates ? dates[1].toDate() : null);
  };

  return (
    <Card>
        
          <Title level={3}>
            <FormOutlined /> Student Feedback
          </Title>
      <Layout>
        <Content>
          <Space style={{ marginBottom: 16 }}>
                {userRole === "superadmin" && (
                      <Select
                        placeholder="Select Branch"
                        value={selectedBranch}
                        onChange={(value) => setSelectedBranch(value)}
                        style={{ width: 200, marginBottom: 16, marginRight: 12 }}
                        allowClear
                      >
                        {branches.map((branch) => (
                          <Option key={branch.id} value={branch.id}>
                            {branch.name}
                          </Option>
                        ))}
                      </Select>
                    )}

            <Input
              placeholder={t('Search by Student Name')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: 200 }}
            />
            <Input
                placeholder="Search by Feedback Ref"
                value={searchFeedbackId}
                onChange={(e) => setSearchFeedbackId(e.target.value)}
                style={{ width: 220 }}
                />
            <RangePicker onChange={handleDateRangeChange} />
            <CSVLink data={currentItems} filename="student_feedback_report.csv">
            <Button icon={<FileExcelOutlined />}>
                {t('Export CSV')}
            </Button>
            </CSVLink>
            <Button
                icon={<ReloadOutlined />}
                onClick={fetchFeedbacks}
            >
                {t('Refresh')}
            </Button>
          </Space>

          <Table
            columns={columns}
            dataSource={currentItems}
             pagination={{
                        pageSize: 50,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        }}
            rowKey={(record, index) => index}
            scroll={{ x: 'max-content' }}
            style={{
              overflowX: 'auto',
              width: '100%',
              maxWidth: '100%'
            }}
          />
       
        </Content>
      </Layout>

      <Drawer
  title="Edit Feedback"
  open={editDrawerVisible}
  onClose={() => setEditDrawerVisible(false)}
  width={600}
  footer={
    <div style={{ textAlign: 'right' }}>
      <Button onClick={() => setEditDrawerVisible(false)} style={{ marginRight: 8 }}>
        Cancel
      </Button>
      <Button
        type="primary"
        onClick={handleSubmitFeedbackUpdate}
        disabled={selectedFeedback?.approve !== 'pending'}
      >
        Save
      </Button>
    </div>
  }
>
  {selectedFeedback && (
    selectedFeedback.approve !== 'pending' ? (
      <Tag color="green" style={{ marginBottom: 16 }}>Approved</Tag>
    ) : (
      <Tag color="orange" style={{ marginBottom: 16 }}>Pending Approval</Tag>
    )
  )}

<Form layout="vertical" disabled={selectedFeedback?.approve !== 'pending'}>
  <Form.Item label="Attendance & Punctuality">
    <Input.TextArea
      value={feedbackFormData.attendance}
      onChange={(e) =>
        setFeedbackFormData({ ...feedbackFormData, attendance: e.target.value })
      }
    />
  </Form.Item>

  <Form.Item label="Class Participation">
    <Input.TextArea
      value={feedbackFormData.participation}
      onChange={(e) =>
        setFeedbackFormData({ ...feedbackFormData, participation: e.target.value })
      }
    />
  </Form.Item>

  <Form.Item label="Comprehension">
    <Input.TextArea
      value={feedbackFormData.comprehension}
      onChange={(e) =>
        setFeedbackFormData({ ...feedbackFormData, comprehension: e.target.value })
      }
    />
  </Form.Item>

  <Form.Item label="Focus & Behavior">
    <Input.TextArea
      value={feedbackFormData.focus}
      onChange={(e) =>
        setFeedbackFormData({ ...feedbackFormData, focus: e.target.value })
      }
    />
  </Form.Item>

  <Form.Item label="Progress Since Last Class">
    <Input.TextArea
      value={feedbackFormData.progress}
      onChange={(e) =>
        setFeedbackFormData({ ...feedbackFormData, progress: e.target.value })
      }
    />
  </Form.Item>

  <Form.Item label="Areas to Improve">
    <Input.TextArea
      value={feedbackFormData.improvement}
      onChange={(e) =>
        setFeedbackFormData({ ...feedbackFormData, improvement: e.target.value })
      }
    />
  </Form.Item>

  <Form.Item label="Teacher’s Comment">
    <Input.TextArea
      value={feedbackFormData.comment}
      onChange={(e) =>
        setFeedbackFormData({ ...feedbackFormData, comment: e.target.value })
      }
    />
  </Form.Item>
</Form>

</Drawer>

{printRecord && (
  <FeedbackPrintModal
    visible={printModalVisible}
    onClose={() => setPrintModalVisible(false)}
    feedback={printRecord}
    classInfo={{
      class_code: printRecord.class_code,
      subject_name: printRecord.subject_name,
      schedule_time: printRecord.schedule_time,
      schedule_hour: printRecord.schedule_hour,
    }}
    teacherInfo={{
      firstname: printRecord.teacher_firstname,
      lastname: printRecord.teacher_lastname,
      nickname: printRecord.teacher_nickname,
    }}
    studentInfo={{
      firstname: printRecord.student_firstname,
      lastname: printRecord.student_lastname,
      nickname: printRecord.student_nickname,
    }}
  />
)}

    </Card>
    
  );
};

export default withRoleAccess(StudentFeedbackReport, ['admin', 'superadmin']);
